import { useTranslation } from 'react-i18next';
import { Button, IconTile, InputSelect } from '@teo/components';
import { Certificate } from '@teo/components/icons';
import { courseTypeLP } from '@/models/courseTypeOptions';
import { axios, publicAxios } from '@/axios';
import { config } from '@/config';
import { newUid, uploadFile, bfsWalkObject } from '@/util';
import { XMLParser } from 'fast-xml-parser';

const ImportLP = ({
  nodeSelected,
  courseDetail,
  setCourseDetail,
  setCourseType,
  courseType,
  setIsLoaderOpen,
  name,
  description,
  setNodes,
}) => {
  const { t } = useTranslation();

  const handleStart = async () => {
    setIsLoaderOpen(true);

    try {
      const files = await uploadFile();
      if (!files?.length) return;

      const path = newUid(20);

      const formData = new FormData();
      formData.append('file', files[0]);
      formData.append(
        'coursecontentId',
        courseDetail?.coursecontentId?.toString() || ''
      );
      formData.append('publicationPath', path || '');

      // Upload using "any" endpoint
      const uploadRes = await axios.post(
        `${config.backend}/import/publication/any`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      const publicationType = uploadRes?.data?.type; // "scorm" | "xapi"

      // Optimistic UI: swap node while processing
      setNodes((nds) =>
        nds?.map((node) =>
          node?.id === nodeSelected?.id
            ? { ...node, type: 'customNodeEmpty' }
            : node
        )
      );

      // Persist publication info on coursecontent
      await axios.put(
        `${config.backend}/coursecontent/${courseDetail?.coursecontentId}`,
        {
          publicationPath: path,
          publicationCourseType: courseType,
          publicationType,
        }
      );

      // Persist course type on course
      await axios.put(`${config.backend}/course/${courseDetail?.id}`, {
        courseType: courseType,
      });

      // Publish
      await axios.post(
        `${config.backend}/publish/${courseDetail?.coursecontentId}`,
        {}
      );

      // If SCORM, try to parse title from imsmanifest.xml
      let title;
      if (publicationType === 'scorm') {
        try {
          const result = await publicAxios.get(
            `${config.s3root}scorm/${courseDetail?.coursecontentId}/${path}/imsmanifest.xml`
          );

          const parser = new XMLParser({
            ignoreAttributes: false,
            isArray: (name) =>
              ['organization', 'resource', 'file', 'item'].includes(name),
          });

          const json = parser.parse(result.data);

          title = bfsWalkObject(json, (value) => {
            if (Object.prototype.hasOwnProperty.call(value, 'title')) {
              const maybe = value.title;
              if (typeof maybe === 'string' && maybe.trim() !== '')
                return maybe;
            }
          });

          // Persist name if we found it
          if (title) {
            await axios.put(
              `${config.backend}/coursecontent/${courseDetail?.coursecontentId}`,
              {
                publicationPath: path,
                publicationCourseType: courseType,
                publicationType,
                name: title,
              }
            );
          }
        } catch (e) {
          // manifest missing/invalid -> ignore, keep existing name
        }
      }

      // Update local state
      setCourseDetail(
        Object.assign({}, courseDetail, {
          publicationPath: path,
          publicationCourseType: courseType,
          publicationType,
          name: title || courseDetail?.name,
        })
      );

      // Restore node
      setNodes((nds) =>
        nds?.map((node) =>
          node?.id === nodeSelected?.id ? { ...node, type: 'customNode' } : node
        )
      );
    } finally {
      setIsLoaderOpen(false);
    }
  };

  if (!courseDetail) return null;

  return (
    <div className="flex flex-col border-t p-5">
      <h4 className="mb-5 text-base">
        {t('pages.learning_path.upload_label')}
      </h4>

      {courseDetail?.id && courseDetail?.publicationPath ? (
        <>
          <div className="mb-3 flex items-center rounded-lg border border-grey-02 p-4">
            <IconTile Icon={Certificate} color="light_blue" size="small_md" />
            <div className="ml-4 flex flex-col">
              <p className="mb-1 max-w-[220px] text-sm font-medium text-black line-clamp-1">
                {courseDetail?.name}
              </p>
              <div>
                <span className="rounded-[4px] bg-[#1E1E2014] px-[6px] py-[2px] text-xs font-medium text-[#00726B]">
                  {(courseDetail?.publicationType || 'SCORM')
                    .toString()
                    .toUpperCase()}
                </span>
              </div>
            </div>
          </div>
          <Button className="" variant="secondary" onClick={handleStart}>
            {t('pages.learning_path.upload_btn_change')}
          </Button>
        </>
      ) : (
        <>
          <div className="mb-5">
            <h3 className="mb-2 text-sm font-normal">
              {t(`pages.create_publication.type_course`)}
            </h3>
            <div id="courseType">
              <InputSelect
                onChange={(courseType) => setCourseType(courseType.value)}
                value={courseTypeLP.find((x) => x.value === courseType)}
                options={courseTypeLP}
              >
                {(options) => <span>{t(`courseTypes.${options?.name}`)}</span>}
              </InputSelect>
            </div>
          </div>
          <p className="mb-3 text-sm text-grey-05">
            {t('pages.learning_path.upload_text')}
          </p>
          <Button className="" onClick={handleStart}>
            {t('pages.learning_path.upload_btn_select')}
          </Button>
        </>
      )}
    </div>
  );
};

export default ImportLP;
